import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  clearFoundGroups,
  clearSearchUsers,
  clearSelectedChatTopic,
  clearSelectedUser,
  getChatBots,
  getChatMentorGroups,
  getChats,
  getChatTopics,
  searchGroups,
  searchUsers,
} from '@/store/chat/actions';

import { selectChat } from '@/store/chat/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Input } from 'antd';
import BotSettingsButton from '@/components/BotSettingsButton/BotSettingsButton';
import MarkdownViewer from '@/components/MarkdownViewer';
import { ArrowLeftOutlined, MoreOutlined } from '@ant-design/icons';

import Aside from '../Aside/Aside';
import ChatModal from '../Messages/components/ChatModal';
import BotElement from './components/BotElement';
import ChatElement from './components/ChatElement';
import NewChat from './components/NewChat';
import PersonalChatElement from './components/PersonalChatElement';
import SearchElement from './components/SearchElement';

import useDebounce from '@shared/hooks/useDebounce';

import Utils from '@/Utils';
import classNames from 'classnames';

import styles from './ChatsList.module.scss';

const ChatsList = () => {
  const [searchValue, setSearchValue] = useState(undefined);
  const [visibleGroupModal, setVisibleGroupModal] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();
  const debounceSearchValue = useDebounce(searchValue, 700);

  const { t } = useTranslation('chat');
  const { foundUsers, selectedUser, groups, foundGroups, chatTopics, pagination, isLoading, chatBots } =
    useSelector(selectChat);
  const { currentUser } = useSelector(selectUsers);
  const [searchParams] = useSearchParams();
  const chatIdRef = useRef(searchParams.get('chatId'));

  const isPersonal = useMemo(() => {
    return location.pathname.includes('chat/personal');
  }, [location.pathname]);

  const isBots = useMemo(() => {
    return location.pathname.includes('chat/bots');
  }, [location.pathname]);

  const isMentoring = useMemo(() => {
    return !location.pathname.includes('personal') && !location.pathname.includes('group') && !isBots;
  }, [location.pathname]);

  useEffect(() => {
    if (isMentoring) {
      const requestId = location.pathname.replace('/chat/', '');
      dispatch(getChatMentorGroups(requestId));
    } else if (isBots) {
      dispatch(getChatBots());
    } else {
      dispatch(getChats({ isPersonal, chatId: chatIdRef.current }));
    }
    dispatch(clearSearchUsers());
  }, [location.pathname]);

  useEffect(() => {
    if (debounceSearchValue) {
      if (isPersonal) {
        dispatch(searchUsers(debounceSearchValue, 0, true));
      } else {
        dispatch(searchGroups(debounceSearchValue));
      }
    } else {
      if (isPersonal) {
        dispatch(clearSearchUsers());
      } else {
        dispatch(clearFoundGroups());
      }
    }
  }, [debounceSearchValue]);

  useEffect(() => {
    setSearchValue(undefined);

    if (selectedUser?.hasTopic) {
      dispatch(getChatTopics(selectedUser.chatId));
    }
  }, [selectedUser]);

  const foundUsersFiltered = useMemo(() => {
    if (foundUsers?.length) {
      const idSetGroups = groups?.length ? new Set(groups?.map(group => group?.userData?.id)) : new Set();
      const idSetFound = new Set(foundUsers?.map(user => user.id));

      idSetGroups.add(currentUser.id);

      const hasGroups = groups?.length ? groups?.filter(group => idSetFound.has(group.userData.id)) : [];
      const notHasGroups = foundUsers?.filter(user => !idSetGroups.has(user.id));
      return [hasGroups, notHasGroups];
    } else {
      return [];
    }
  }, [foundUsers]);

  // Функция для подгрузки следующих чатов/групп
  const loadMoreGroups = () => {
    if (!isLoading.getChats) {
      if (isMentoring) {
        const requestId = location.pathname.replace('/chat/', '');
        dispatch(getChatMentorGroups(requestId));
      } else {
        dispatch(getChats({ isPersonal, chatId: chatIdRef.current }, (pagination?.getChats?.page || 1) + 1));
      }
    }
  };

  const renderGroups = () => {
    if (foundUsersFiltered.length) {
      return (
        <>
          {foundUsersFiltered[0]?.map((group, index) => (
            <PersonalChatElement key={index} data={group} />
          ))}
          {!!foundUsersFiltered[0].length && !!foundUsersFiltered[1].length && (
            <div className={styles.groups__global}>{t('inCompany')}</div>
          )}
          {foundUsersFiltered[1]?.map((user, index) => (
            <SearchElement key={index} data={user} />
          ))}
        </>
      );
    }

    if (foundGroups.length) {
      return foundGroups?.map((group, index) => <ChatElement key={index} data={group} />);
    }

    if (!foundUsers?.length && searchValue && debounceSearchValue) {
      return (
        <MarkdownViewer
          className={styles.groups__notFound}
          children={t(isPersonal ? 'notFoundUsers' : 'notFoundGroups')}
        />
      );
    }

    if (groups?.length || chatBots?.length) {
      let list = [];

      if (isPersonal || isMentoring) {
        list = groups?.map((group, index) => (
          <PersonalChatElement isMentoring={isMentoring} key={index} data={group} />
        ));
      } else if (isBots) {
        if (Utils.isAdminOrSuperAdmin(currentUser)) {
          list = chatBots?.map((bot, index) => <BotElement key={index} data={bot} />);
        } else {
          list = chatBots
            ?.filter(e => e?.botName !== 'Помощник-методист')
            ?.map((bot, index) => <BotElement key={index} data={bot} />);
        }
      } else {
        list = groups?.map((group, index) => <ChatElement key={index} data={group} />);
      }

      return (
        <InfiniteScroll
          initialLoad={false}
          loadMore={loadMoreGroups}
          hasMore={groups.length < (pagination?.getChats?.totalElements || 0)}
          useWindow={false}
        >
          {list}
        </InfiniteScroll>
      );
    }

    return null;
  };

  const handleCloseChatTopic = () => {
    dispatch(clearSelectedUser());
    dispatch(clearSelectedChatTopic());
  };

  return (
    <div className={styles.groups}>
      {!selectedUser?.hasTopic ? (
        <div className={styles.groups__header}>
          <p data-qa='messageTitle'>{t('message')}</p>
          {!isPersonal && !isMentoring && !isBots && <NewChat />}
        </div>
      ) : (
        <div className={styles.groups__headerTopic}>
          <ArrowLeftOutlined onClick={handleCloseChatTopic} className={styles.groups__icon} />
          <div className={styles.groups__box} onClick={() => setVisibleGroupModal(true)}>
            <div className={styles.groups__info}>
              <p>{Utils.renderWithTooltip(selectedUser.chatName, 25)}</p>
              <p className={styles.groups__countChatUsers}>
                {t('users')}: {selectedUser?.countChatUsers}
              </p>
            </div>

            <MoreOutlined className={styles.groups__icon} />
          </div>
        </div>
      )}
      {!isMentoring && !selectedUser?.hasTopic && <Aside />}
      {!isMentoring && !selectedUser?.hasTopic && (
        <div className={styles.groups__search}>
          <Input.Search
            value={searchValue}
            placeholder={t('searchPlaceholder')}
            onChange={event => setSearchValue(event.target.value)}
            data-qa='inputSearch'
          />
        </div>
      )}
      <div className={classNames(styles.groups__mainBox, { [styles.groups__mainBox_topics]: selectedUser?.hasTopic })}>
        <div className={classNames(styles.groups__main, { [styles.groups__main_small]: selectedUser?.hasTopic })}>
          {renderGroups()}
        </div>
        {selectedUser?.hasTopic && (
          <div className={classNames(styles.groups__main, styles.groups__main_middle)}>
            {chatTopics.map((chat, index) => (
              <ChatElement key={index} data={chat} isTopic />
            ))}
          </div>
        )}
      </div>
      {!!selectedUser?.chatName && selectedUser?.hasTopic && (
        <ChatModal onClose={() => setVisibleGroupModal(false)} visible={visibleGroupModal} />
      )}
      {isBots && <BotSettingsButton />}
    </div>
  );
};

export default ChatsList;
